import { c as _c } from "react/compiler-runtime";
import { Loader2 } from "lucide-react";
import { FeatureTypeFilterChips } from "@/lib/components/admin/FeatureTypeFilterChips";
import { ReviewDetailPane } from "@/lib/components/admin/ReviewDetailPane";
import { ReviewList } from "@/lib/components/admin/ReviewList";
import { ReviewMap } from "@/lib/components/admin/ReviewMap";
import { ReviewQueueTabs } from "@/lib/components/admin/ReviewQueueTabs";
import { useIsAdmin } from "@/lib/hooks/useIsAdmin";
import { useReviewDashboard } from "@/lib/hooks/useReviewDashboard";

/**
 * Admin review dashboard at /admin/granska.
 *
 * Two-column layout:
 * - Left: tabs + (list | map) — controlled by the dashboard hook.
 * - Right: detail pane for the selected item.
 *
 * All logic lives in `useReviewDashboard`; this view is pure presentation.
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export function ReviewDashboardView() {
  const $ = _c(43);
  const isAdmin = useIsAdmin();
  const dash = useReviewDashboard();
  if (!isAdmin) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = /*#__PURE__*/_jsx("div", {
        className: "container mx-auto flex min-h-[60vh] items-center justify-center px-4",
        children: /*#__PURE__*/_jsx("p", {
          className: "text-gray-500",
          children: "Du har inte beh\xF6righet."
        })
      });
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  let t0;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = /*#__PURE__*/_jsx("h1", {
      className: "text-2xl font-bold text-gray-900",
      children: "Granska"
    });
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = /*#__PURE__*/_jsx("kbd", {
      className: "rounded bg-gray-100 px-1",
      children: "j"
    });
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = /*#__PURE__*/_jsx("kbd", {
      className: "rounded bg-gray-100 px-1",
      children: "k"
    });
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = /*#__PURE__*/_jsx("kbd", {
      className: "rounded bg-gray-100 px-1",
      children: "e"
    });
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = /*#__PURE__*/_jsx("kbd", {
      className: "rounded bg-gray-100 px-1",
      children: "r"
    });
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = /*#__PURE__*/_jsxs("div", {
      className: "mb-4",
      children: [t0, /*#__PURE__*/_jsxs("p", {
        className: "mt-1 text-xs text-gray-500",
        children: ["Tangenter: ", t1, "/", t2, " n\xE4sta/f\xF6reg\xE5ende \xB7", " ", t3, " godk\xE4nn \xB7", " ", t4, " avvisa \xB7", " ", /*#__PURE__*/_jsx("kbd", {
          className: "rounded bg-gray-100 px-1",
          children: "m"
        }), " v\xE4xla karta"]
      })]
    });
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== dash.counts || $[8] !== dash.setTab || $[9] !== dash.setView || $[10] !== dash.tab || $[11] !== dash.view) {
    t6 = /*#__PURE__*/_jsx(ReviewQueueTabs, {
      tab: dash.tab,
      onTabChange: dash.setTab,
      view: dash.view,
      onViewChange: dash.setView,
      counts: dash.counts
    });
    $[7] = dash.counts;
    $[8] = dash.setTab;
    $[9] = dash.setView;
    $[10] = dash.tab;
    $[11] = dash.view;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== dash.counts || $[14] !== dash.featureTypeCounts || $[15] !== dash.setTypeFilter || $[16] !== dash.tab || $[17] !== dash.typeFilter) {
    t7 = (dash.tab === "features" || dash.tab === "all") && /*#__PURE__*/_jsx(FeatureTypeFilterChips, {
      counts: dash.featureTypeCounts,
      active: dash.typeFilter,
      onChange: dash.setTypeFilter,
      totalCount: dash.counts.features
    });
    $[13] = dash.counts;
    $[14] = dash.featureTypeCounts;
    $[15] = dash.setTypeFilter;
    $[16] = dash.tab;
    $[17] = dash.typeFilter;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== dash.isLoading || $[20] !== dash.items || $[21] !== dash.select || $[22] !== dash.selection || $[23] !== dash.view) {
    t8 = /*#__PURE__*/_jsx("div", {
      className: "flex-1 overflow-hidden",
      children: dash.isLoading ? /*#__PURE__*/_jsx("div", {
        className: "flex h-full items-center justify-center",
        children: /*#__PURE__*/_jsx(Loader2, {
          className: "h-6 w-6 animate-spin text-gray-400"
        })
      }) : dash.view === "list" ? /*#__PURE__*/_jsx("div", {
        className: "h-full overflow-y-auto",
        children: /*#__PURE__*/_jsx(ReviewList, {
          items: dash.items,
          selection: dash.selection,
          onSelect: dash.select
        })
      }) : /*#__PURE__*/_jsx(ReviewMap, {
        items: dash.items,
        selection: dash.selection,
        onSelect: dash.select
      })
    });
    $[19] = dash.isLoading;
    $[20] = dash.items;
    $[21] = dash.select;
    $[22] = dash.selection;
    $[23] = dash.view;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== t6 || $[26] !== t7 || $[27] !== t8) {
    t9 = /*#__PURE__*/_jsxs("div", {
      className: "flex min-h-[70vh] flex-col border-r",
      children: [t6, t7, t8]
    });
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== dash.approve || $[30] !== dash.closeRejectDialog || $[31] !== dash.isApproving || $[32] !== dash.isRejecting || $[33] !== dash.isRepositioning || $[34] !== dash.openRejectDialog || $[35] !== dash.reject || $[36] !== dash.rejectDialogOpen || $[37] !== dash.reposition || $[38] !== dash.selection) {
    t10 = /*#__PURE__*/_jsx("div", {
      className: "min-h-[70vh]",
      children: /*#__PURE__*/_jsx(ReviewDetailPane, {
        selection: dash.selection,
        isApproving: dash.isApproving,
        isRejecting: dash.isRejecting,
        isRepositioning: dash.isRepositioning,
        onApprove: dash.approve,
        onReject: dash.reject,
        onReposition: dash.reposition,
        rejectDialogOpen: dash.rejectDialogOpen,
        onOpenRejectDialog: dash.openRejectDialog,
        onCloseRejectDialog: dash.closeRejectDialog
      })
    });
    $[29] = dash.approve;
    $[30] = dash.closeRejectDialog;
    $[31] = dash.isApproving;
    $[32] = dash.isRejecting;
    $[33] = dash.isRepositioning;
    $[34] = dash.openRejectDialog;
    $[35] = dash.reject;
    $[36] = dash.rejectDialogOpen;
    $[37] = dash.reposition;
    $[38] = dash.selection;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  let t11;
  if ($[40] !== t10 || $[41] !== t9) {
    t11 = /*#__PURE__*/_jsxs("div", {
      className: "container mx-auto px-4 py-6",
      children: [t5, /*#__PURE__*/_jsxs("div", {
        className: "grid grid-cols-1 overflow-hidden rounded-lg border bg-white lg:grid-cols-[380px_1fr]",
        children: [t9, t10]
      })]
    });
    $[40] = t10;
    $[41] = t9;
    $[42] = t11;
  } else {
    t11 = $[42];
  }
  return t11;
}